import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Register from './components/auth/Register';
import VerifyEmail from './components/auth/VerifyEmail';
import InvitedPasswordSetup from './components/auth/InvitedPasswordSetup';
import VerificationRoute from './components/VerificationRoute';
import RootRedirect from './components/RootRedirect';
import DashboardLayout from './components/layout/DashboardLayout';
import CreateEnvelope from './components/documents/CreateEnvelope';
import Drafts from './components/envelopes/Drafts';
import EnvelopeProgress from './components/envelopes/EnvelopeProgress';
import VerifyAccess from './components/signing/VerifyAccess';
import SignDocument from './components/signing/SignDocument';
import Templates from './components/templates/Templates';
import CreateTemplate from './components/templates/CreateTemplate';
import PlansPage from './components/billing/PlansPage';
import PlanCheckoutPage from './components/billing/PlanCheckoutPage';
import BillingPage from './components/billing/BillingPage';
import OrganizationUsersPage from './components/organization/OrganizationUsersPage';
import OrganizationRolesPage from './components/organization/OrganizationRolesPage';
import TeamDashboard from './components/team-dashboard/TeamDashboard';
import OrgAdminRoute from './components/OrgAdminRoute';
import EmbedRoute from './components/embed/EmbedRoute';

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/verify-email"
        element={
          <VerificationRoute>
            <VerifyEmail />
          </VerificationRoute>
        }
      />
      <Route path="/auth/complete-invited-password-setup" element={<InvitedPasswordSetup />} />

      {/* Public Signing Routes (no auth required) */}
      <Route path="/sign/:recipientId" element={<VerifyAccess />} />
      <Route path="/sign/:recipientId/document" element={<SignDocument />} />

      {/* Mobile WebView embed: auth via ?token= (same Bearer token as API) */}
      <Route
        path="/embed/create-envelope/:envelopeId?"
        element={
          <EmbedRoute>
            <CreateEnvelope />
          </EmbedRoute>
        }
      />
      <Route
        path="/embed/create-template/:templateId?"
        element={
          <EmbedRoute>
            <CreateTemplate />
          </EmbedRoute>
        }
      />
      
      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OrgAdminRoute message="Team dashboard is only available to organization admins.">
                <TeamDashboard />
              </OrgAdminRoute>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-envelope/:envelopeId?"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateEnvelope />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Templates />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/envelopes/drafts"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Drafts />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/envelopes/sent"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Drafts />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/envelopes/completed"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Drafts />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/envelopes/revoked"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Drafts />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/envelopes/:id/progress"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EnvelopeProgress />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-template/:templateId?"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateTemplate />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OrgAdminRoute message="Only organization admins can view and change plans.">
                <PlansPage />
              </OrgAdminRoute>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans/checkout"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OrgAdminRoute message="Only organization admins can upgrade or change the organization plan.">
                <PlanCheckoutPage />
              </OrgAdminRoute>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OrgAdminRoute message="Only organization admins can manage billing and subscription.">
                <BillingPage />
              </OrgAdminRoute>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OrganizationUsersPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/roles"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OrganizationRolesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Root path - checks auth and redirects accordingly */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* Catch all - redirect to root which will handle auth check */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
};

export default AppRoutes;

